import { Button, Card, Col, Form, Input, Row, Select, Space, Tooltip } from 'antd';
import DisplayAlert from '../../components/Commons/Alert';
import GenericTable from '../../components/Commons/Table/generic-table.component';
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import ShowDataLength from '../../components/Commons/Table/results-length-table.component';
import { useState } from 'react';
import { getAll, remove } from '../../services/locations.service';
import { PaginationProps } from 'antd/lib';
import { FilterValue } from 'antd/es/table/interface';
import { useNavigate } from 'react-router-dom';
import { AlertService } from '../../services/alert.service';
import { useMutation, useQuery } from 'react-query';
import ConfirmModal from '../../components/Commons/Modal/confirm-modal.component';
import { cutLongText } from '../../services/utils';
import ProfilePermissionService from "../../services/profilePermissions.service";
import { isMobile } from 'react-device-detect';
import FilterButton from '../../components/Commons/Table/FilterButton';

const { Search } = Input;

export default function Locations(props: any) {
    const [locations, setLocations] = useState([]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number | undefined>(10);
    const [total, setTotal] = useState<number>(0);
    const [search, setSearch] = useState<string>('');
    const [order, setOrder] = useState<string>('name-asc');
    const [removeLocation, setRemoveLocation] = useState<any | undefined>(undefined);
    const [zoneId, setZoneId] = useState<number | undefined>(undefined);
    const [formSearch] = Form.useForm();
    const [formFilter] = Form.useForm();

    const navigate = useNavigate();

    const profilePermissionService = ProfilePermissionService.getInstance();

    const { isLoading: locationsLoading, refetch: loadLocations } = useQuery(
        ['loadLocations', currentPage, search, order, perPage, zoneId],
        () => getAll({ page: currentPage, per_page: perPage, order, search, zone_id: zoneId }),
        {
            onSuccess: response => {
                setLocations(response.data.map((location: any) => {
                    return {
                        key: location.data.id,
                        ...location.data,
                        ...location.relationships
                    }
                }));

                setCurrentPage(response.meta.current_page);
                setPerPage(response.meta.per_page);
                setTotal(response.meta.total);
            },
            refetchOnWindowFocus: false
        }
    );

    const { mutate: RemoveLocationMutate } = useMutation(remove,
        {
            onSuccess: () => {
                AlertService.sendAlert([
                    { text: 'Local apagado com sucesso.' }
                ]);

                setRemoveLocation(undefined);

                if (locations.length === 1 && currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                } else {
                    loadLocations();
                }
            }
        }
    );

    const onSearch = (search_value: any) => {
        setCurrentPage(1);
        setSearch(search_value);
    };

    const onChangeFilters = () => {
        setCurrentPage(1);
        setZoneId(formFilter.getFieldValue('filter_zone'));
    };

    const onClearFilters = () => {
        setCurrentPage(1);
        setZoneId(undefined);
        formFilter.setFieldValue('filter_zone', undefined)
    };

    const handleTableChange = (
        pagination: PaginationProps,
        filters: Record<string, FilterValue | null>,
        sorter: any
    ) => {
        if (pagination.current != currentPage) {
            setCurrentPage(Number(pagination.current));
        }

        if (pagination.pageSize != perPage) {
            setPerPage(pagination.pageSize);
            setCurrentPage(1);
        }

        if (sorter.order) {
            let orderBy = `${sorter.field}-${sorter.order.substr(0, sorter.order.length - 3)}`;

            if (order != orderBy) {
                setOrder(orderBy);
            }
        }
    };

    const toPage = (page: string, location_id: number = 0) => {
        let pages = {
            create: '/routes/locations/create',
            edit: `/routes/locations/${location_id}/edit`
        };

        navigate(pages[page as keyof typeof pages]);
    };

    const filters = (
        <Form
            form={formFilter}
            name="filter_locations"
            layout="vertical"
            className="form_filter_horizontal"
            autoComplete="off"
            style={{ width: 300 }}>
            <Row style={{ width: '100%' }}>
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item label="Zona" name="filter_zone" className="item_filter">
                        <Select placeholder="Todos" value={zoneId} style={{ width: '100%' }} size="large">
                            {props.zones.map((zone: any) => <Select.Option key={zone.id} value={zone.id}>{zone.name}</Select.Option>)}
                        </Select>
                    </Form.Item>

                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Button onClick={onChangeFilters} size="large">Filtrar</Button>

                    {zoneId && <Button type="link" onClick={onClearFilters} size="large">Limpar</Button>}
                </Col>
            </Row>
        </Form>
    );

    const columns: any = [
        {
            title: 'Local', dataIndex: 'name', sorter: true, key: 'name',
            render: (_: any, record: any) => (cutLongText(record.name, 40)),
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Morada', dataIndex: 'address', sorter: true, key: 'address',
            render: (_: any, record: any) => (cutLongText(record.address, 40)),
            responsive: ['lg', 'xl'],
        },
        {
            title: 'Zona', dataIndex: 'zone_id', sorter: true, key: 'zone_id',
            render: (_: any, record: any) => (cutLongText(record.zone.name, 40)),
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Ações', key: 'action',
            render: (_: any, record: any) => (
                profilePermissionService.hasPermission("routes-locations:write") &&
                <Space size="small" direction={isMobile ? 'vertical' : 'horizontal'}>
                    <Tooltip placement="top" title="Editar">
                        <Button
                            type="default" shape="circle" size="middle" icon={<EditOutlined />}
                            onClick={() => toPage('edit', record.id)} />
                    </Tooltip>

                    <Tooltip placement="top" title="Apagar">
                        <Button type="default" shape="circle" size="middle" icon={<DeleteOutlined />}
                            onClick={() => setRemoveLocation(record)} />
                    </Tooltip>
                </Space>
            ),
            width: '15%',
        }
    ];

    return (
        <>
            <DisplayAlert />
            <Card bodyStyle={{ padding: 8 }}>
                <div>
                    <Row className="search_bar">
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                            <Form
                                form={formSearch}
                                name="search_locations"
                                layout="vertical"
                                className="form_search_horizontal"
                                autoComplete="off">

                                <Form.Item name="search_location" className={!isMobile ? "item_search" : "item_search_mobile"}>
                                    <Search onSearch={onSearch} placeholder="Pesquisa por local ou morada" enterButton="Pesquisar" allowClear size="large" maxLength={255} />
                                </Form.Item>
                            </Form>
                        </Col>

                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 12 }}
                            style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {profilePermissionService.hasPermission("routes-locations:write") &&
                                <Button type="primary" onClick={() => toPage('create')} size="large"
                                    style={isMobile ? {
                                        marginTop: 5, marginBottom: -30
                                    } : {}}
                                    block={isMobile}>Criar novo</Button>
                            }
                        </Col>
                    </Row>
                </div>

                <div className="table">
                    <GenericTable
                        columns={columns}
                        dataSource={locations}
                        pagination={{
                            current: currentPage,
                            pageSize: perPage,
                            showSizeChanger: true,
                            total: total
                        }}
                        loading={locationsLoading}
                        totalChildren={
                            <div className="zone_result_size_n_export" style={isMobile ? { marginTop: 10 } : {}}>
                                <ShowDataLength size={total} className="show_result_size" />

                                <FilterButton filters={filters} />
                            </div>
                        }
                        empty={{
                            createTheFirst: !locations.length && !search,
                            textOne: 'Esta tabela encontra-se vazia, uma vez que ainda não foram criadoas locais.',
                            textTwo: profilePermissionService.hasPermission("routes-zones:write")
                                ? `Para começar, crie um novo local.`
                                : '',
                            link: profilePermissionService.hasPermission("routes-zones:write")
                                ? '/routes/locations/create'
                                : '',
                            createText: 'Criar novo'
                        }}
                        handleTableChange={handleTableChange}
                    />

                    <ConfirmModal
                        open={!!removeLocation}
                        title={`Tem certeza que deseja apagar o local "${removeLocation?.name}"?`}
                        okText="Apagar" cancelText="Voltar"
                        onOk={() => RemoveLocationMutate(removeLocation?.id)}
                        onCancel={() => setRemoveLocation(undefined)}
                    ></ConfirmModal>
                </div>
            </Card>
        </>
    );
}